'use client';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { loadFromStorage, lsKeys, saveToStorage } from '~features/storage';
import { useIsEmailConfirmed } from '~features/tags';
import { useUserId } from '~hooks/useUserId';
import {
	emailConfirmActions,
	emailConfirmSelectors,
} from '~store/email-confirm';

import { useEmailConfirmationWallLocation } from './useEmailConfirmationWallLocation';

export const useEmailConfirmModal = (): {
	isOpened: boolean;
	open: (force?: boolean) => void;
	close: () => void;
} => {
	const userId = useUserId();
	const dispatch = useDispatch();
	const [initUrl, setInitUrl] = useState('');

	const opened = useSelector(emailConfirmSelectors.getIsModalOpened);
	const { saveLocation } = useEmailConfirmationWallLocation();

	const [confirmed, loading] = useIsEmailConfirmed();

	useEffect(() => {
		setInitUrl(window.location.toString());
	}, []);

	const open = useCallback(
		(force?: boolean) => {
			const hideModal = loadFromStorage(lsKeys.HIDE_EMAIL_CONFIRM_MODAL, false);
			if (hideModal && !force) {
				return;
			}

			saveLocation(initUrl);
			dispatch(emailConfirmActions.showModal());
		},
		[dispatch, initUrl, saveLocation]
	);

	const close = useCallback(() => {
		saveToStorage(lsKeys.HIDE_EMAIL_CONFIRM_MODAL, true);
		dispatch(emailConfirmActions.hideModal());
	}, [dispatch]);

	const isOpened = !!userId && opened && !loading && !confirmed;

	useEffect(() => {
		if (isOpened) {
			dispatch(emailConfirmActions.storeModalShown());
		}
	}, [dispatch, isOpened]);

	return { isOpened, open, close };
};
